import { Icon, Portal, useDisclosure } from '@chakra-ui/react';
import {
  RuleValidationErrorLevelEnum,
  RuleValidationField,
} from '@monetize/types/app';
import { FC, useEffect, useRef, useState } from 'react';
import { BsExclamationCircleFill } from 'react-icons/bs';
import { MdArrowForward, MdClose } from 'react-icons/md';
import {
  MBox,
  MButton,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
  MIcon,
  MText,
  MVStack,
} from '../../../components/Monetize';
import { BannerAlertType } from '../../../components/Monetize/MAlertTag';
import { IQuoteValidation } from '../../../types';
import {
  findQuoteFieldAnchor,
  focusQuoteField,
  getValidationFieldLabel,
  getValidationFields,
  QUOTE_VALIDATION_COLOR,
} from '../../../utils/quoteFieldValidations';

interface ValidationGroup {
  title: string;
  color: string;
  backColor: string;
  alertType: BannerAlertType;
  validations: IQuoteValidation[];
  errorLevel: RuleValidationErrorLevelEnum;
}

const ValidationPopover = ({ validation }: { validation: ValidationGroup }) => {
  if (validation.validations.length === 0) {
    return null;
  }
  return (
    <MBox
      minW="5"
      minH="5"
      w="5"
      h="5"
      background={validation.color}
      borderRadius="50%"
      mr="1"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      data-testid="validation-popover-trigger"
    >
      <MText color="white" fontSize="xs">
        {validation.validations.length}
      </MText>
    </MBox>
  );
};

interface QuoteAlertsProps {
  validations: IQuoteValidation[];
  showDivider?: boolean;
  /**
   * Off where the quote page is not mounted behind the drawer (the approvals
   * dashboard), so we never go looking for anchors that belong to another
   * screen.
   */
  showFieldLinks?: boolean;
}

const QuoteAlerts: FC<QuoteAlertsProps> = ({
  validations,
  showDivider = true,
  showFieldLinks = true,
}: QuoteAlertsProps) => {
  const {
    open: isOpen,
    onOpen,
    onClose,
  } = useDisclosure({
    onOpen: () => {},
  });
  /** Set on click, consumed once the drawer has finished closing. */
  const pendingFieldKeyRef = useRef<string | null>(null);
  const [jumpTargetsByRuleId, setJumpTargetsByRuleId] = useState<
    Map<string, RuleValidationField[]>
  >(new Map());

  /**
   * Fields we can actually jump to, by rule. Resolved after the drawer opens
   * (the quote page is mounted behind it) so we never offer a link to a field
   * this quote does not render — PO Number with the setting off, Amendment
   * Date on a non-amendment, a custom field hidden by permissions. Reading the
   * DOM is a side effect, so it belongs here rather than in render.
   */
  useEffect(() => {
    if (!isOpen || !showFieldLinks) {
      // Keep the identity when already empty, so this cannot churn renders if
      // `validations` arrives as a fresh array each time.
      setJumpTargetsByRuleId((current) =>
        current.size === 0 ? current : new Map(),
      );
      return;
    }
    const targets = new Map<string, RuleValidationField[]>();
    validations.forEach((validation) => {
      const fields = getValidationFields(validation).filter(({ key }) =>
        findQuoteFieldAnchor(key),
      );
      if (fields.length > 0) {
        targets.set(validation.ruleId, fields);
      }
    });
    setJumpTargetsByRuleId(targets);
  }, [isOpen, showFieldLinks, validations]);

  const errorValidations = {
    title: 'Error',
    color: QUOTE_VALIDATION_COLOR[RuleValidationErrorLevelEnum.ERROR],
    backColor: 'tRed.paleRed',
    alertType: 'error' as BannerAlertType,
    validations: [] as IQuoteValidation[],
    errorLevel: RuleValidationErrorLevelEnum.ERROR,
  };
  const warnValidations = {
    title: 'Warning',
    color: QUOTE_VALIDATION_COLOR[RuleValidationErrorLevelEnum.WARN],
    backColor: 'tOrange.input',
    alertType: 'warning' as BannerAlertType,
    validations: [] as IQuoteValidation[],
    errorLevel: RuleValidationErrorLevelEnum.WARN,
  };
  const infoValidations = {
    title: 'Information',
    color: QUOTE_VALIDATION_COLOR[RuleValidationErrorLevelEnum.INFO],
    backColor: 'tBlue.hover',
    alertType: 'info' as BannerAlertType,
    validations: [] as IQuoteValidation[],
    errorLevel: RuleValidationErrorLevelEnum.INFO,
  };

  validations.forEach((validation) => {
    if (validation.errorLevel === RuleValidationErrorLevelEnum.ERROR) {
      errorValidations.validations.push(validation);
    } else if (validation.errorLevel === RuleValidationErrorLevelEnum.WARN) {
      warnValidations.validations.push(validation);
    } else if (validation.errorLevel === RuleValidationErrorLevelEnum.INFO) {
      infoValidations.validations.push(validation);
    }
  });

  const onCloseDrawer = () => {
    onClose();
  };

  if (!validations || validations.length === 0) {
    return null;
  }

  const hasErrors = errorValidations.validations.length > 0;

  return (
    <MFlex>
      <MButton variant="tertiary" mr="1.5" onClick={onOpen}>
        <MText mr="2" color={hasErrors ? 'tRed.base' : 'tRoyalBlue.base'}>
          {hasErrors ? 'Action Required' : 'Quote Alerts'}
        </MText>
        <ValidationPopover validation={errorValidations} />
        <ValidationPopover validation={warnValidations} />
        <ValidationPopover validation={infoValidations} />
      </MButton>

      {showDivider && (
        <MDivider
          borderColor="tPurple.base"
          height="5"
          w="1"
          mx="2"
          orientation="vertical"
        />
      )}

      <MDrawer
        open={isOpen}
        placement="end"
        onOpenChange={(e) => {
          if (!e.open) {
            onCloseDrawer();
          }
        }}
        onExitComplete={() => {
          if (pendingFieldKeyRef.current) {
            focusQuoteField(pendingFieldKeyRef.current);
            pendingFieldKeyRef.current = null;
          }
        }}
        size="sm"
        preventScroll={false}
      >
        <Portal>
          <MDrawerOverlay />
          <MDrawerPositioner>
            <MDrawerContent>
              <MDrawerCloseButton>
                <Icon asChild>
                  <MdClose />
                </Icon>
              </MDrawerCloseButton>
              {/* Header is shorter than the theme default; keep theme px so the
              bottom border spans full width. */}
              <MDrawerHeader py="2.5">
                <MFlex
                  justifyContent="space-between"
                  alignItems="center"
                  minH={8}
                >
                  <MText fontSize="inherit" fontWeight="inherit">
                    Quote Alerts ({validations.length})
                  </MText>
                </MFlex>
              </MDrawerHeader>
              <MDrawerBody px="4" bgColor="tGray.sidebarDark">
                <MBox w="full">
                  {[errorValidations, warnValidations, infoValidations].map(
                    (validationGroup, index) => (
                      <MVStack key={`validationGroup-${index}`} w="full">
                        {validationGroup.validations.map(
                          (validation, valIndex) => (
                            <MBox
                              w="full"
                              p={4}
                              borderRadius="3px"
                              key={valIndex}
                              bg="white"
                              display="flex"
                              mb="2"
                            >
                              <MIcon
                                width={4}
                                height={4}
                                as={BsExclamationCircleFill}
                                color={validationGroup.color}
                              />
                              <MBox ml="4">
                                <MText
                                  color="tGray.acBlack"
                                  fontSize="md"
                                  fontWeight="bold"
                                  mb="1"
                                >
                                  {validationGroup.title}
                                </MText>
                                <MText color="tGray.acBlack" fontSize="sm">
                                  {validation.message}
                                </MText>
                                {jumpTargetsByRuleId.has(validation.ruleId) && (
                                  <MVStack
                                    alignItems="flex-start"
                                    gap={1}
                                    mt="2"
                                  >
                                    {jumpTargetsByRuleId
                                      .get(validation.ruleId)
                                      ?.map((validationField) => (
                                        <MButton
                                          key={validationField.key}
                                          variant="link"
                                          leftIcon={
                                            <MIcon as={MdArrowForward} />
                                          }
                                          onClick={() => {
                                            pendingFieldKeyRef.current =
                                              validationField.key;
                                            onCloseDrawer();
                                          }}
                                        >
                                          Jump to{' '}
                                          {getValidationFieldLabel(
                                            validationField,
                                          )}
                                        </MButton>
                                      ))}
                                  </MVStack>
                                )}
                              </MBox>
                            </MBox>
                          ),
                        )}
                      </MVStack>
                    ),
                  )}
                </MBox>
              </MDrawerBody>

              <MDivider />

              <MDrawerFooter>
                <MBox w="full">
                  <MFlex justifyContent="center">
                    <MButton
                      mt={5}
                      variant="cancel"
                      justifySelf="center"
                      onClick={onCloseDrawer}
                    >
                      Close
                    </MButton>
                  </MFlex>
                </MBox>
              </MDrawerFooter>
            </MDrawerContent>
          </MDrawerPositioner>
        </Portal>
      </MDrawer>
    </MFlex>
  );
};

export { QuoteAlerts };
